import { useEffect, useMemo, useState } from "react";
import "./App.css";

const assetUrl = (folder, file) =>
  `${window.location.origin}/${folder}/${encodeURIComponent(file)}`;

const fontLabel = (file) => file.replace(/\.(ttf|otf)$/i, "").replace(/[_-]+/g, " ");
const fontFormat = (file) => (/\.otf$/i.test(file) ? "opentype" : "truetype");

// Responsive deploy v2: layout full-width mengikuti layar di semua zoom
function App() {
  const [fonts, setFonts] = useState([]);
  const [icons, setIcons] = useState([]);
  const [images, setImages] = useState([]);
  const [text1, setText1] = useState("Sample Text");
  const [text2, setText2] = useState("");
  const [selectedFont, setSelectedFont] = useState("system-ui");
  const [text2Pos, setText2Pos] = useState("left");
  const [fontSize, setFontSize] = useState("30");
  const [spacing, setSpacing] = useState(-10);
  const [showFontNames, setShowFontNames] = useState(true);
  const [activeMenu, setActiveMenu] = useState("fonts");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.dataset.build = "v2-responsive";
    let cancelled = false;
    Promise.all(
      ["fonts", "icons", "images"].map((type) =>
        fetch(`/${type}/`, { cache: "no-store" }).then((res) => {
          if (!res.ok) throw new Error(`${type}: HTTP ${res.status}`);
          return res.json();
        })
      )
    )
      .then(([fontData, iconData, imageData]) => {
        if (cancelled) return;
        setFonts(Array.isArray(fontData) ? fontData : []);
        setIcons(Array.isArray(iconData) ? iconData : []);
        setImages(Array.isArray(imageData) ? imageData : []);
        setError("");
      })
      .catch((err) => !cancelled && setError(`Aset belum dapat dimuat (${err.message}).`))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const fontFaces = useMemo(
    () => fonts.map((file) => `@font-face{font-family:"${fontLabel(file).replace(/"/g, "")}";src:url("${assetUrl("fonts", file)}") format("${fontFormat(file)}");font-display:swap;}`).join("\n"),
    [fonts]
  );

  const menuItems = [
    ["fonts", "Aa", "Fonts", fonts.length],
    ["icons", "✦", "Icons", icons.length],
    ["images", "▧", "Images", images.length],
  ];

  return (
    <main className="app-shell">
      <style>{fontFaces}</style>
      <header className="hero">
        <div>
          <p className="eyebrow">AKRILIKWOW CREATIVE TOOLS</p>
          <h1>Font Ku</h1>
          <p className="subtitle">Preview font, icon, dan aset desain secara cepat sebelum dipakai.</p>
        </div>
        <div className="asset-total"><strong>{fonts.length + icons.length + images.length}</strong><span>aset tersedia</span></div>
      </header>

      <nav className="tabs" aria-label="Pilih aset">
        {menuItems.map(([key, icon, label, count]) => (
          <button key={key} className={activeMenu === key ? "tab active" : "tab"} onClick={() => setActiveMenu(key)}>
            <span className="tab-icon">{icon}</span>{label}<span className="count">{count}</span>
          </button>
        ))}
      </nav>

      {error && <div className="notice error">{error} Silakan refresh setelah folder aset diperbaiki.</div>}
      {loading && <div className="notice">Menyelaraskan aset…</div>}

      {activeMenu === "fonts" && !loading && (
        <section>
          <div className="controls">
            <label className="control wide"><span>Teks utama</span><input value={text1} onChange={(e) => setText1(e.target.value)} placeholder="Tulis teks preview" /></label>
            <label className="control wide"><span>Teks kedua</span><input value={text2} onChange={(e) => setText2(e.target.value)} placeholder="Tulis teks kedua" /></label>
            <label className="control"><span>Font kedua</span><select value={selectedFont} onChange={(e) => setSelectedFont(e.target.value)}><option value="system-ui">System UI</option>{fonts.map((file) => <option key={file} value={fontLabel(file)}>{fontLabel(file)}</option>)}</select></label>
            <label className="control"><span>Posisi teks kedua</span><select value={text2Pos} onChange={(e) => setText2Pos(e.target.value)}><option value="left">Kiri</option><option value="center">Tengah</option><option value="right">Kanan</option></select></label>
            <label className="control compact"><span>Ukuran</span><input type="number" value={fontSize} onChange={(e) => setFontSize(e.target.value.replace(/\D/g, ""))} onBlur={() => setFontSize(String(Math.max(10, Math.min(100, Number(fontSize) || 30))))} min="10" max="100" /></label>
            <label className="control compact"><span>Jarak</span><input type="number" value={spacing} onChange={(e) => setSpacing(Number(e.target.value) || 0)} min="-50" max="50" /></label>
            <button className="toggle" onClick={() => setShowFontNames(!showFontNames)}>{showFontNames ? "Sembunyikan nama" : "Tampilkan nama"}</button>
          </div>
          <div className="section-heading"><div><h2>Font library</h2><p>{fonts.length} font tersinkronisasi dari server</p></div></div>
          <div className="font-grid">
            {fonts.map((file) => {
              const name = fontLabel(file);
              return <article className="font-card" key={file}>
                <p className="preview primary" title={text1 || "Sample Text"} style={{ fontFamily: `"${name}", system-ui`, fontSize: `${Number(fontSize) || 30}px` }}>{text1 || "Sample Text"}</p>
                <p className={`preview secondary pos-${text2Pos}`} title={text2} style={{ fontFamily: selectedFont, marginTop: `${spacing}px` }}>{text2}</p>
                {showFontNames && <p className="font-name" title={file}>{name}</p>}
              </article>;
            })}
          </div>
        </section>
      )}

      {activeMenu === "icons" && <AssetGrid items={icons} folder="icons" type="icon" />}
      {activeMenu === "images" && <AssetGrid items={images} folder="images" type="image" />}
    </main>
  );
}

function AssetGrid({ items, folder, type }) {
  return <section><div className="section-heading"><div><h2>{type === "icon" ? "Icon library" : "Image library"}</h2><p>{items.length} aset tersedia</p></div></div><div className="asset-grid">{items.map((file) => <article className="asset-card" key={file}><img src={assetUrl(folder, file)} alt={file} loading="lazy" /><p title={file}>{file}</p></article>)}</div></section>;
}

export default App;
